/**
 * In-app notifications (M2): the bell. No polling: the realtime socket (app/realtime.tsx)
 * receives the unread count whenever it changes and refetches the list; the list is also
 * refreshed on window focus and after a reconnect. Marking read updates the cache in place.
 */
import {
  NotificationsResponseSchema,
  OkResponseSchema,
  type NotificationsResponse,
} from '@aroo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from './client';

const enc = encodeURIComponent;

export const notificationKeys = {
  list: ['notifications'] as const,
  /** The Notifications page (more rows); under `list`, so the bell's refreshes reach it too. */
  all: ['notifications', 'all'] as const,
};

/** How many the Notifications page shows (the bell shows the API's default 50). */
export const NOTIFICATIONS_PAGE_LIMIT = 300;

export function getNotifications(): Promise<NotificationsResponse> {
  return apiGet('/notifications', NotificationsResponseSchema);
}

export function getAllNotifications(): Promise<NotificationsResponse> {
  return apiGet(`/notifications?limit=${NOTIFICATIONS_PAGE_LIMIT}`, NotificationsResponseSchema);
}

export function useAllNotifications() {
  return useQuery({
    queryKey: notificationKeys.all,
    queryFn: getAllNotifications,
    refetchOnWindowFocus: true,
    staleTime: 20_000,
  });
}

export function markNotificationRead(id: string) {
  return apiPost(`/notifications/${enc(id)}/read`, OkResponseSchema);
}

export function markAllNotificationsRead() {
  return apiPost('/notifications/read-all', OkResponseSchema);
}

export function useNotifications(enabled = true) {
  return useQuery({
    queryKey: notificationKeys.list,
    queryFn: getNotifications,
    enabled,
    refetchOnWindowFocus: true,
    staleTime: 20_000,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: markNotificationRead,
    onMutate: (id: string) => {
      const now = new Date().toISOString();
      qc.setQueriesData<NotificationsResponse>({ queryKey: notificationKeys.list }, (prev) =>
        prev
          ? {
              items: prev.items.map((n) =>
                n.id === id && n.readAt === null ? { ...n, readAt: now } : n,
              ),
              unread: Math.max(
                0,
                prev.unread - (prev.items.some((n) => n.id === id && n.readAt === null) ? 1 : 0),
              ),
            }
          : prev,
      );
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: notificationKeys.list }),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: markAllNotificationsRead,
    onMutate: () => {
      const now = new Date().toISOString();
      qc.setQueriesData<NotificationsResponse>({ queryKey: notificationKeys.list }, (prev) =>
        prev
          ? {
              items: prev.items.map((n) => (n.readAt === null ? { ...n, readAt: now } : n)),
              unread: 0,
            }
          : prev,
      );
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: notificationKeys.list }),
  });
}
